import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ShieldCheck, Star } from 'lucide-react';
import { COMPANY_INFO, fetchCollection } from '../data';
import FormSection from '../components/FormSection';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadContactPage() {
      try {
        setLoading(true);
        const pages = await fetchCollection('pages');
        const matched = pages.find((p) => p.slug?.da === 'kontakt' || p.title === 'Kontakt');
        setPageData(matched);
      } catch (error) {
        console.error('Error loading Contact page:', error);
      } finally {
        setLoading(false);
      }
    }
    loadContactPage();
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-brand-dark/50 pt-20">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-blue border-t-transparent mb-4" />
        <p className="text-base">Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="pt-20 space-y-20">
      {/* Hero Section */}
      <section className="relative pt-12 pb-10 sm:pt-16 sm:pb-14 overflow-hidden bg-brand-deep text-white">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-glarmestre-firmabil.jpg"
            alt="To smilende glarmestre fra Energirude Eksperten foran firmabilen"
            className="w-full h-full object-cover object-[center_35%]"
          />
          {/* Stærkere mørk/neutral overlay */}
          <div className="absolute inset-0 bg-slate-950/75 sm:bg-slate-950/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-900/65" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7 text-left space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold tracking-wider text-brand-light uppercase bg-white/15 border border-white/25 px-3.5 py-1.5 rounded-full inline-block backdrop-blur-xs shadow-2xs">
                Kontakt os
              </span>
              <span className="text-xs font-semibold text-white/80 bg-brand-blue/30 border border-brand-blue/40 px-3 py-1 rounded-full">
                Base i Smørum – dækker Egedal, Nordsjælland og store dele af Sjælland
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
              {pageData?.meta_title || 'Kontakt med Energirude Eksperten | Få et Prisstærkt Tilbud'}
            </h1>
            <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl">
              Kontakt os for et prisstærkt tilbud. Med base i Smørum leverer <Link to="/" className="text-white hover:text-brand-light underline font-semibold">Energirude Eksperten</Link> kvalitetsarbejde til konkurrencedygtige priser i Egedal, Nordsjælland og store dele af Sjælland.
            </p>
          </div>

          <div className="lg:col-span-5">
            {/* Trustpilot / Anbefalinger Box */}
            <div className="bg-white/95 backdrop-blur-sm border border-white/20 p-6 rounded-2xl space-y-4 shadow-xl flex flex-col items-center text-center overflow-hidden max-w-md mx-auto lg:max-w-none">
              <div className="-mx-6 -mt-6 w-[calc(100%+3rem)]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=90,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-kundeanmeldelser-trustpilot.png"
                  alt="Anbefalinger og kundeanmeldelser af Energirude Eksperten"
                  className="w-full h-auto object-cover block"
                />
              </div>
              <div className="space-y-1 pt-2">
                <p className="text-sm font-bold text-brand-deep">Mere end 50 anbefalinger</p>
                <p className="text-xs text-brand-dark/70 leading-relaxed">
                  Vi er stolte af at have over 4 stjerner i gennemsnit baseret på mere end 50 fremragende anmeldelser fra vores glade kunder.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Contact Details & Verbatim Content */}
          <div className="lg:col-span-6 space-y-8">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-brand-deep">Start med en snak</h2>
              <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
                Du er meget velkommen til at udfylde vores kontaktformular eller ringe for spørgsmål eller et uforpligtende bygge-tilbud. Vores glarmester hos <Link to="/" className="text-brand-deep hover:text-brand-blue font-semibold underline decoration-brand-blue/30 underline-offset-2 transition-colors">Energirude Eksperten</Link> har base i Smørum og kører ud til kunder i Egedal, Nordsjælland og store dele af Sjælland. Vi vender hurtigst muligt tilbage til dig.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-brand-warm/30 border border-brand-light p-6 rounded-2xl space-y-2">
                <h3 className="font-bold text-brand-deep text-sm sm:text-base">Ring til os</h3>
                <a
                  href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
                  className="block font-semibold text-brand-blue hover:text-brand-deep transition-colors text-sm sm:text-base whitespace-nowrap"
                >
                  {COMPANY_INFO.phone}
                </a>
                <p className="text-xs text-brand-dark/50">Mandag - Søndag: 08:00 - 18:00</p>
              </div>

              <div className="bg-brand-warm/30 border border-brand-light p-6 rounded-2xl space-y-2">
                <h3 className="font-bold text-brand-deep text-sm sm:text-base">Skriv en mail</h3>
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="block font-semibold text-brand-blue hover:text-brand-deep transition-colors text-sm sm:text-base whitespace-nowrap"
                >
                  {COMPANY_INFO.email}
                </a>
                <p className="text-xs text-brand-dark/50">Vi svarer altid inden for 24 timer</p>
              </div>
            </div>

            {/* Address & CVR */}
            <div className="bg-brand-light/30 border border-brand-light p-6 rounded-2xl space-y-4">
              <div className="flex items-start gap-3">
                <div>
                  <h4 className="font-bold text-brand-deep text-sm sm:text-base">Adresse</h4>
                  <p className="text-sm text-brand-dark/80 mt-0.5">{COMPANY_INFO.address}</p>
                  <p className="text-xs text-brand-blue font-semibold mt-1">Base i Smørum – vi dækker Egedal, Nordsjælland og store dele af Sjælland</p>
                </div>
              </div>
              <div className="pt-3 border-t border-brand-light flex items-center justify-between text-xs text-brand-dark/60">
                <span>CVR: {COMPANY_INFO.cvr}</span>
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-4 h-4 text-brand-green" />
                  Glarmestergaranti
                </span>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-6">
            <FormSection
              title="Skriv til os"
              subtitle="Udfyld formularen, så vender vi hurtigst muligt tilbage med svar eller et uforpligtende tilbud."
            />
          </div>
        </div>
      </section>
    </div>
  );
}
